import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import Loader from '../../services/loader';

const List = forwardRef((props, ref) => {
  const {
    id,
    data,
    columns,
    onInit,
    onRowClick,
    pagination,
    onDataUpdated,
    loading,
  } = props;
  const loader = useRef(new Loader('#scormContentLayout'));
  const [sourceData, setSourceData] = useState(data);
  const [isLoading, setLoading] = useState(loading);
  const [isFirstInitiation, setFirstInitiation] = useState(true);

  const initTable = () => {
    const config = {
      data: {
        type: 'local',
        source: sourceData,
        pageSize: 20,
      },

      layout: {
        theme: 'default',
        class: '',
        scroll: false,
        footer: false,
      },

      sortable: true,

      pagination,
      pagingType: 'full_numbers',
      columns,
      rows: onRowClick
        ? {
            beforeTemplate: (row, rowData) =>
              row
                .addClass('clickable-row')
                .on('click', () => onRowClick(rowData)),
          }
        : undefined,
    };

    const tableElement = $(`#${id}`);
    tableElement.mDatatable(config);

    if (onInit) {
      setTimeout(() => {
        onInit();
      }, 1000);
    }
  };

  useImperativeHandle(ref, () => ({
    getList() {
      return sourceData;
    },
    setList(list) {
      setSourceData(list);
    },
    getListItem(position) {
      return sourceData[position];
    },
    addListItem(item) {
      setSourceData(previousItems => [...previousItems, item]);
    },
    updateListItem(position, item) {
      const list = [...sourceData];
      list[position] = item;
      setSourceData(list);
    },
    removeListItem(position) {
      const list = [...sourceData];
      list.splice(position, 1);
      setSourceData(list);
    },
    setLoading(loadingStatus) {
      setLoading(loadingStatus);
    },
    destroy() {
      const table = $(`#${id}`).mDatatable();
      table.destroy();
    },
  }));

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  useEffect(() => {
    if (isFirstInitiation) {
      initTable();
      setFirstInitiation(false);
    } else {
      $(`#${id}`).mDatatable().destroy();
      initTable();
      onDataUpdated(sourceData);
    }
  }, [sourceData]);

  return <div className="m_datatable" id={id} />;
});

List.propTypes = {
  /** DataTable div container id */
  id: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  /** Column definitions, passed directly to jQuery component, see it's docs */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      textAlign: PropTypes.string,
      width: PropTypes.number,
      sortable: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      overflow: PropTypes.string,
      template: PropTypes.func,
    }),
  ).isRequired,

  pagination: PropTypes.bool,
  /**
   * initialization event handler so we can do extra work, like add events to the
   * components rendered as a string
   */
  onInit: PropTypes.func,
  /**
   * Row click event handler, row data is passed as an argument
   */
  onRowClick: PropTypes.func,
  /** Whether we're currently loading new data */
  loading: PropTypes.bool.isRequired,
  onDataUpdated: PropTypes.func,
};

List.defaultProps = {
  onInit: null,
  onRowClick: null,
  pagination: true,
  onDataUpdated: null,
};

export default List;
